import type { ThemeVariant } from '../../contract.js';

// Luce has its own sidebar palette — black with amber brand. Shared
// across both variants so the sidebar stays dark on mode flip.
const SIDEBAR_LUCE = {
  sidebar: '#000000',
  sidebarText: '#F5D547',
  sidebarMute: '#6B6B6B',
  sidebarActive: '#F5D547',
};

const RADII = { sm: '6px', md: '10px', lg: '14px', xl: '24px' };

// `mono` mirrors `sans` — see packages/skins/src/styles.css for why.
const SANS_STACK = "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', system-ui, sans-serif";
const FONTS = {
  sans: SANS_STACK,
  mono: SANS_STACK,
};

const MOTION = {
  fill: 'cubic-bezier(0.22, 1, 0.36, 1)',
  flow: 'linear',
  pulse: 'ease-out',
  press: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

export const luceLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    colors: {
      bg: '#F2EDE4',
      surface: '#FAF6EC',
      surface2: '#EDE6D6',
      sidebar: '#1A1814',
      sidebarText: '#F5D547',
      sidebarMute: '#8B8875',
      sidebarActive: '#F5D547',
      border: '#D4CEB8',
      borderStrong: '#B8B098',
      accent: '#B8860B',
      accentSoft: 'rgba(184, 134, 11, 0.15)',
      // Dark goldenrod accent — white text reads cleanly on it and
      // matches the existing CTA pattern. Preserved at the
      // pre-existing literal so this contract addition doesn't
      // introduce a visual regression on Luce.
      onAccent: '#FFFFFF',
      charging: '#C5680A',
      chargingGlow: 'rgba(197, 104, 10, 0.35)',
      power: '#CA8A04',
      info: '#1E40AF',
      warn: '#B91C1C',
      good: '#166534',
      busy: '#C2410C',
      mute: '#6B6558',
      text: '#1C1A15',
      textMute: '#5F584B',
      // Luce light runs warmer cream surfaces; the amber glow stays
      // low-alpha so it reads as a tint against the cream rather than
      // a halo. Same 3-stop structure (rim + near bloom + soft drop)
      // as the Default variants so the visual language holds across
      // theme families.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #B8860B 14%, transparent),' +
        ' 0 0 18px -4px color-mix(in srgb, #B8860B 18%, transparent),' +
        ' 0 6px 20px -10px color-mix(in srgb, #B8860B 16%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #B8860B 7%, transparent),' +
        ' 0 0 10px -6px color-mix(in srgb, #B8860B 10%, transparent),' +
        ' 0 3px 10px -8px color-mix(in srgb, #B8860B 7%, transparent)',
      // Luce light widget chrome. Warm cream surface (rather than the
      // pure-white Default light uses) ties the card to Luce's cream
      // page bg; gold-tinted border + warm-toned drop shadow with a
      // dark-goldenrod 1px ring keep the Ferrari-instrument warmth
      // even at a frosted-glass alpha. Same reduced blur (8px) as
      // Default light to avoid muddy fog over the light basemap.
      widgetBg: 'rgba(252, 248, 240, 0.82)',
      widgetBorder: 'rgba(184, 134, 11, 0.18)',
      widgetShadow:
        '0 8px 24px rgba(60, 40, 0, 0.15),' +
        ' 0 0 0 1px rgba(184, 134, 11, 0.12)',
      widgetBlur: 'blur(8px) saturate(110%)',
      // Luce light chrome tier — warm cream with gold-tinted border
      // so popovers/menus pick up the Ferrari-instrument warmth even
      // at near-opaque alpha. Warm-toned drop shadow (rgba 60,40,0)
      // keeps the cards reading "lit by gold lamps" rather than the
      // cool LED feel of pure-black shadow.
      chromeBg: 'rgba(252, 248, 240, 0.94)',
      chromeBorder: 'rgba(184, 134, 11, 0.14)',
      chromeShadow: '0 4px 16px rgba(60, 40, 0, 0.12)',
      chromeBlur: 'blur(12px) saturate(110%)',
    },
    radii: RADII,
    fonts: FONTS,
    motion: MOTION,
  },
};

export const luceDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    colors: {
      // Lifted from pure black toward the same charcoal spread Default
      // dark uses. Pure #000 backgrounds looked oppressive and made
      // the amber card glow read as a ring on empty space; a modest
      // lift to #15 / #1E / #282820 preserves the Ferrari-instrument
      // cluster feel while giving the accent glow something to bloom
      // against. Borders bumped in lockstep so card edges stay defined.
      bg: '#151413',
      surface: '#1E1D1B',
      surface2: '#282620',
      ...SIDEBAR_LUCE,
      border: '#2E2B25',
      borderStrong: '#3F3B33',
      accent: '#F5D547',
      accentSoft: 'rgba(245, 213, 71, 0.15)',
      // Bright yellow accent — white CTA text on this is technically
      // low-contrast (~1.1:1, WCAG fail), but that's the long-standing
      // shipped behaviour. Preserved here to scope this contract
      // addition strictly to the Electric Light / Dark Electric ask;
      // a separate Luce-contrast follow-up can flip this to a dark
      // value (e.g. '#1A1814') after a visual review.
      onAccent: '#FFFFFF',
      charging: '#F5D547',
      chargingGlow: 'rgba(245, 213, 71, 0.55)',
      power: '#F5D547',
      info: '#60A5FA',
      warn: '#E63946',
      good: '#4ADE80',
      busy: '#F59E0B',
      mute: '#6B6B6B',
      text: '#FFFFFF',
      textMute: '#A0A0A0',
      // Amber-tinted glow stack — same 3-stop structure as Default
      // dark (rim + near bloom + soft drop) so the card lighting
      // vocabulary reads the same in both themes. Higher alphas than
      // Luce light because the surface is near-black.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #F5D547 20%, transparent),' +
        ' 0 0 24px -4px color-mix(in srgb, #F5D547 32%, transparent),' +
        ' 0 8px 32px -12px color-mix(in srgb, #F5D547 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #F5D547 10%, transparent),' +
        ' 0 0 14px -6px color-mix(in srgb, #F5D547 16%, transparent),' +
        ' 0 4px 12px -8px color-mix(in srgb, #F5D547 12%, transparent)',
      // Luce dark widget chrome. Warm near-black bg (rather than the
      // cool 20,20,28 Default uses) ties the card to Luce's
      // charcoal page bg; amber-tinted 1px ring on the drop shadow
      // preserves the brand halo. Warmer shadow tone (rgba(20,12,0))
      // than Default dark's pure black so the cards feel lit by the
      // same amber accent rather than a cool LED.
      widgetBg: 'rgba(28, 22, 14, 0.75)',
      widgetBorder: 'rgba(245, 213, 71, 0.12)',
      widgetShadow:
        '0 12px 32px rgba(20, 12, 0, 0.45),' +
        ' 0 0 0 1px rgba(245, 213, 71, 0.10)',
      widgetBlur: 'blur(16px) saturate(160%)',
      // Luce dark chrome tier — warm near-black surface with amber-
      // tinted hairline. Warmer shadow tone (rgba 20,12,0) keeps the
      // amber-lit feel through the popover stack. 0.92 alpha matches
      // Default dark chrome opacity so the dense-glass recipe reads
      // the same across families.
      chromeBg: 'rgba(28, 22, 14, 0.92)',
      chromeBorder: 'rgba(245, 213, 71, 0.10)',
      chromeShadow: '0 8px 24px rgba(20, 12, 0, 0.50)',
      chromeBlur: 'blur(16px) saturate(160%)',
    },
    radii: RADII,
    fonts: FONTS,
    motion: MOTION,
  },
};
